/**
 * Compact multi-select of the organization's people for the automations
 * builder: who an `assign_people` assigns, a notify emails or a comment
 * mentions. A popover trigger showing the selection count + a searchable
 * checkbox list, read from the people directory the server offers this person.
 * For `assign_people` an empty selection is valid and means "clear the column"
 * when the action runs.
 */
import { Button, Popover, PopoverContent, PopoverTrigger } from '@monksflow/monks-ui';
import { useQuery } from '@tanstack/react-query';
import { Check } from 'lucide-react';
import { useMemo, useState } from 'react';

import { trackerPeopleQueryOptions, type TrackerPerson } from '@/entities/tracker-automation';
import { SearchInput } from '@/shared/ui/SearchInput';
import { useTranslation } from 'react-i18next';

interface AutomationPeoplePickerProps {
  value: string[];
  onChange: (next: string[]) => void;
  /** What the button reads with nobody picked; by default, what an empty assignment does. */
  emptyLabel?: string;
}

export function AutomationPeoplePicker({
  value,
  onChange,
  emptyLabel,
}: AutomationPeoplePickerProps) {
  const { t } = useTranslation('trackers');

  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const usersQuery = useQuery(trackerPeopleQueryOptions());
  const users = useMemo<TrackerPerson[]>(() => usersQuery.data ?? [], [usersQuery.data]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return q ? users.filter((u) => u.name.toLowerCase().includes(q)) : users;
  }, [users, search]);

  // Someone picked before they left the organization has no row in the roster, so
  // without one of their own they would count in the label and could never be
  // unticked. Held back until the roster loads, when everyone would look gone.
  const former = useMemo(() => {
    if (!usersQuery.isSuccess) return [];
    const onRoster = new Set(users.map((u) => u.id));
    return value.filter((id) => !onRoster.has(id));
  }, [usersQuery.isSuccess, users, value]);

  const toggle = (id: string) =>
    onChange(value.includes(id) ? value.filter((v) => v !== id) : [...value, id]);

  const label =
    value.length === 0
      ? (emptyLabel ?? t('trackersBoard.automationPeoplePicker.anyoneOrClear'))
      : value.length === 1
        ? (users.find((u) => u.id === value[0])?.name ??
          t('trackersBoard.automationPeoplePicker.personCount', { count: 1 }))
        : t('trackersBoard.automationPeoplePicker.personCount', { count: value.length });

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button variant="outline" size="default" className="h-9 flex-1 justify-start font-normal">
          {label}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-64 p-2" align="start">
        <SearchInput
          aria-label={t('trackersBoard.automationPeoplePicker.searchPeopleLabel')}
          placeholder={t('trackersBoard.automationPeoplePicker.searchPeople')}
          value={search}
          onChange={setSearch}
          size="sm"
          className="mb-2"
        />
        <div className="flex max-h-56 flex-col gap-0.5 overflow-y-auto">
          {former.map((id) => (
            <button
              key={id}
              type="button"
              aria-pressed
              onClick={() => toggle(id)}
              className="flex items-center justify-between gap-2 rounded-md px-2 py-1.5 text-left text-sm text-muted-foreground transition-colors hover:bg-accent"
            >
              <span className="truncate italic">
                {t('trackersBoard.automationPeoplePicker.formerMember')}
              </span>
              <Check className="size-4 shrink-0 text-primary" aria-hidden />
            </button>
          ))}
          {filtered.map((u) => {
            const selected = value.includes(u.id);
            return (
              <button
                key={u.id}
                type="button"
                aria-pressed={selected}
                onClick={() => toggle(u.id)}
                className="flex items-center justify-between gap-2 rounded-md px-2 py-1.5 text-left text-sm transition-colors hover:bg-accent"
              >
                <span className="truncate">{u.name}</span>
                {selected ? <Check className="size-4 shrink-0 text-primary" aria-hidden /> : null}
              </button>
            );
          })}
          {filtered.length === 0 && former.length === 0 ? (
            <p className="px-2 py-1.5 text-xs text-muted-foreground">
              {t('trackersBoard.automationPeoplePicker.noPeopleFound')}
            </p>
          ) : null}
        </div>
      </PopoverContent>
    </Popover>
  );
}
